import React from "react";
import {
  Calculator,
  Calendar,
  ChevronsLeft,
  ChevronsRight,
  History,
  IdCard,
  ListChecks,
  Receipt,
  StickyNote,
  User,
} from "lucide-react";

import { CalculatorKeypad } from "../../../components/shared/CalculatorKeypad";
import {
  CALENDAR_FIXTURE,
  DISCOVERY_FIXTURE,
  LEAD_FIXTURE,
  NOTES_FIXTURE,
  QUOTE_FIXTURE,
  RIGHT_TOOL_KEYS,
} from "../workspaceFixtures";
import { WORKSPACE_TOOLS, WORKSPACE_TOOL_PROJECTION_STATUSES } from "../projectionContract";
import CalendarToolPanel, { CalendarSetApptButton } from "./panels/CalendarToolPanel";
import DiscoveryPanel from "./panels/DiscoveryPanel";
import LeadInspectorPanel from "./panels/LeadInspectorPanel";
import NotesPanel from "./panels/NotesPanel";
import QuotePanel from "./panels/QuotePanel";

// Safe fallback if this card is ever rendered without toolProjections
// (no current call site does — AgentWorkspaceShell always supplies the
// composed view model's toolProjections). Matches each panel's own
// zero-prop default behavior: "ok" status, no data override.
const DEFAULT_TOOL_PROJECTION = Object.freeze({
  status: WORKSPACE_TOOL_PROJECTION_STATUSES.OK,
  reason: null,
  data: null,
});

/**
 * AW-ORIGINAL-RESTORE-001. The fixture fallbacks moved HERE, from the
 * panels' own default parameters.
 *
 * The panels are shared presentation now and must not carry a fixture
 * of any kind -- a default that silently substitutes synthetic customer
 * data is exactly what made them unusable outside this shell. So each
 * `??` below is the same substitution that used to happen inside the
 * panel, performed explicitly by the one component that is entitled to
 * do it: the development-only Agent Tools card.
 *
 * Design-rehearsal behaviour is therefore unchanged -- the same values
 * reach the same props -- while Production, which never renders this
 * file, now gets a panel that fails closed instead of inventing a
 * prospect.
 */

// Matches the established icon-chip treatment from the Script Runner Quick
// Reference rail (frontend/src/pages/script-play/components/QuickReferenceRail.jsx).
const SELECTED_ICON_CLASS = "bg-indigo-500/90 text-white hover:bg-indigo-500";
const IDLE_ICON_CLASS = "bg-white/10 text-slate-100 hover:bg-white/15";

// Standardized MissionAssist domain-language order for this pass: Lead
// (source-record info) and Discovery (info collected during this call) sit
// first, since agents open them most while reading the Script; Calculator
// is new; the rest keep their prior relative order.
const TOOLS = Object.freeze([
  { key: RIGHT_TOOL_KEYS.LEAD, label: "Lead", Icon: IdCard },
  { key: RIGHT_TOOL_KEYS.DISCOVERY, label: "Discovery", Icon: User },
  { key: RIGHT_TOOL_KEYS.QUOTE, label: "Quote", Icon: Receipt },
  { key: RIGHT_TOOL_KEYS.CALCULATOR, label: "Calculator", Icon: Calculator },
  { key: RIGHT_TOOL_KEYS.CALENDAR, label: "Calendar", Icon: Calendar },
  { key: RIGHT_TOOL_KEYS.ACTIONS, label: "Actions", Icon: ListChecks },
  { key: RIGHT_TOOL_KEYS.NOTES, label: "Notes", Icon: StickyNote },
  { key: RIGHT_TOOL_KEYS.HISTORY, label: "History", Icon: History },
]);

function ToolPanelContent({ toolKey, toolProjections }) {
  switch (toolKey) {
    case RIGHT_TOOL_KEYS.LEAD: {
      const projection = toolProjections[WORKSPACE_TOOLS.LEAD] || DEFAULT_TOOL_PROJECTION;
      return (
        <LeadInspectorPanel
          customerInformation={projection.data?.customerInformation ?? LEAD_FIXTURE.customerInformation}
          address={projection.data?.address ?? LEAD_FIXTURE.address}
          leadInformation={projection.data?.leadInformation ?? LEAD_FIXTURE.leadInformation}
          projectionStatus={{ status: projection.status, reason: projection.reason }}
        />
      );
    }
    case RIGHT_TOOL_KEYS.DISCOVERY: {
      const projection = toolProjections[WORKSPACE_TOOLS.DISCOVERY] || DEFAULT_TOOL_PROJECTION;
      return (
        <DiscoveryPanel
          subjects={projection.data?.subjects ?? DISCOVERY_FIXTURE.subjects}
          fields={projection.data?.fields ?? DISCOVERY_FIXTURE.fields}
          values={projection.data?.values ?? DISCOVERY_FIXTURE.values}
          projectionStatus={{ status: projection.status, reason: projection.reason }}
        />
      );
    }
    case RIGHT_TOOL_KEYS.QUOTE: {
      const projection = toolProjections[WORKSPACE_TOOLS.QUOTE] || DEFAULT_TOOL_PROJECTION;
      return (
        <QuotePanel
          subjects={projection.data?.subjects ?? QUOTE_FIXTURE.subjects}
          options={projection.data?.options ?? QUOTE_FIXTURE.options}
          fields={projection.data?.fields ?? QUOTE_FIXTURE.fields}
          values={projection.data?.values ?? QUOTE_FIXTURE.values}
          projectionStatus={{ status: projection.status, reason: projection.reason }}
        />
      );
    }
    case RIGHT_TOOL_KEYS.CALCULATOR:
      return (
        <CalculatorKeypad
          density="compact"
          testId="workspace-calculator-panel"
          displayTestId="workspace-calculator-display"
          gridTestId="workspace-calculator-grid"
          buttonTestIdPrefix="workspace-calc"
          showHistory
          historyTestId="workspace-calculator-history"
        />
      );
    case RIGHT_TOOL_KEYS.CALENDAR: {
      const projection = toolProjections[WORKSPACE_TOOLS.CALENDAR] || DEFAULT_TOOL_PROJECTION;
      return (
        <CalendarToolPanel
          initialSelectedDate={projection.data?.selectedDate ?? CALENDAR_FIXTURE.selectedDate}
          appointments={projection.data?.appointments ?? CALENDAR_FIXTURE.appointments}
          projectionStatus={{ status: projection.status, reason: projection.reason }}
        />
      );
    }
    case RIGHT_TOOL_KEYS.ACTIONS:
      return <p className="text-xs text-slate-500">No actions available yet.</p>;
    case RIGHT_TOOL_KEYS.NOTES: {
      const projection = toolProjections[WORKSPACE_TOOLS.NOTES] || DEFAULT_TOOL_PROJECTION;
      return (
        <NotesPanel
          currentUser={projection.data?.currentUser ?? NOTES_FIXTURE.currentUser}
          notes={projection.data?.notes ?? NOTES_FIXTURE.notes}
          projectionStatus={{ status: projection.status, reason: projection.reason }}
        />
      );
    }
    case RIGHT_TOOL_KEYS.HISTORY:
      return <p className="text-xs text-slate-500">No history to show yet.</p>;
    default:
      return null;
  }
}

/**
 * Agent Tools: ONE unified card containing the selected tool's content and
 * the icon rail, separated only by an internal divider — matching the
 * established MissionAssist card language (no external gap or separate
 * sibling card). Collapsing removes the content region from layout so the
 * Main Card reclaims the width; the rail surface always remains visible.
 * Local presentation state only.
 */
export default function WorkspaceAgentToolsCard({
  activeTool,
  onSelectTool,
  isExpanded,
  onToggleExpanded,
  toolProjections = {},
}) {
  const activeToolItem = TOOLS.find((tool) => tool.key === activeTool) || null;

  return (
    <div
      className="glass flex min-h-0 shrink-0 overflow-hidden rounded-xl"
      data-testid="workspace-agent-tools"
    >
      {isExpanded ? (
        <div
          className="flex w-72 flex-col overflow-hidden"
          data-testid="workspace-agent-tools-content"
        >
          <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2.5">
            <p className="text-sm font-semibold text-slate-100">
              {activeToolItem ? activeToolItem.label : "Agent Tools"}
            </p>
            {activeToolItem?.key === RIGHT_TOOL_KEYS.CALENDAR ? <CalendarSetApptButton /> : null}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3" data-testid="workspace-tool-panel">
            {activeToolItem ? (
              <ToolPanelContent toolKey={activeToolItem.key} toolProjections={toolProjections} />
            ) : (
              <p className="text-xs text-slate-600">Select a tool to view its details.</p>
            )}
          </div>
        </div>
      ) : null}

      <div
        className={`flex min-h-0 w-[72px] shrink-0 flex-col items-center px-2 py-3 ${
          isExpanded ? "border-l border-white/10" : ""
        }`}
        data-testid="workspace-agent-tools-rail"
      >
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse Agent Tools" : "Expand Agent Tools"}
          data-testid="workspace-agent-tools-toggle"
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
            isExpanded ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS
          }`}
        >
          {isExpanded ? (
            <ChevronsRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        <div className="mt-3 h-px w-7 shrink-0 bg-white/10" aria-hidden="true" />

        {/* Tool icons scroll independently of the collapse/expand toggle
            above, so every tool remains reachable when the Workspace's
            available height shrinks (e.g. at higher browser zoom) — the
            toggle stays fixed; only this list scrolls. */}
        <div
          className="mt-3 flex min-h-0 w-full flex-1 flex-col items-center gap-2.5 overflow-y-auto"
          role="group"
          aria-label="Agent tools"
        >
          {TOOLS.map((tool) => (
            <button
              key={tool.key}
              type="button"
              aria-pressed={activeTool === tool.key}
              onClick={() => onSelectTool(tool.key)}
              data-testid={`workspace-tool-${tool.key}`}
              className="flex flex-col items-center gap-1 rounded-lg text-slate-400 transition-colors hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            >
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
                  activeTool === tool.key ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS
                }`}
              >
                <tool.Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="text-[10px] leading-none text-slate-400">{tool.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
